/**
 * PublicShop.tsx — Public-facing online shop for a business
 * URL: /shop/:businessId
 *
 * No auth required. Displays active products for a business.
 * Customers can browse, add to cart, and place orders.
 */
import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API_URL from '../../config/api';
import { hasCompleteBankDetails, parseBankDetails, normalizeBankDetails, type BankDetails } from '../../utils/bankDetails';
import { getProductImages, type ShopProduct } from '../../utils/shopProduct';
import ProductImageGallery from '../../components/shop/ProductImageGallery';
import {
  clearShopSession,
  isWholesaleShopCustomer,
  readShopCustomer,
  readShopToken,
  syncShopSession,
  type ShopCustomer,
} from '../../utils/shopSession';
import './PublicShop.css';

interface Product extends ShopProduct {}

interface CartItem {
  product: Product;
  qty: number;
  saleType: 'unit' | 'box';
}

const PublicShop: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cart, setCart] = useState<CartItem[]>(() => {
    try {
      const saved = businessId ? localStorage.getItem(`cart_${businessId}`) : null;
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [showCart, setShowCart] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [orderPlaced, setOrderPlaced] = useState(false);

  // Shipping — staff-set only; customers do not edit delivery charges in wholesale shop
  const shippingCost = 0;
  const VAT_RATE = 0.20;
  const [hoveredProduct, setHoveredProduct] = useState<string | null>(null);

  // Payment method
  const [paymentMethod, setPaymentMethod] = useState<'Bank Transfer' | 'Cash on Collection' | 'Part Payment'>('Bank Transfer');
  const [depositAmount, setDepositAmount] = useState('');

  // Customer portal — refreshed from CRM on each visit (Retail ↔ Wholesale)
  const [shopToken, setShopToken] = useState<string | null>(() => (businessId ? readShopToken(businessId) : null));
  const [shopCustomer, setShopCustomer] = useState<ShopCustomer | null>(() => (businessId ? readShopCustomer(businessId) : null));
  const isWholesaleCustomer = isWholesaleShopCustomer(shopCustomer?.customer_type);

  // Customer contact form
  const [customerName, setCustomerName] = useState(() => readShopCustomer(businessId || '')?.name || '');
  const [customerEmail, setCustomerEmail] = useState(() => readShopCustomer(businessId || '')?.email || '');
  const [customerPhone, setCustomerPhone] = useState('');
  const [orderNotes, setOrderNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [shopInfo, setShopInfo] = useState<{ name: string; email: string; phone: string; logo: string; bankDetails: BankDetails | null } | null>(null);
  const businessName = shopInfo?.name || 'Online Shop';

  // Persist cart to localStorage whenever it changes
  useEffect(() => {
    if (businessId) localStorage.setItem(`cart_${businessId}`, JSON.stringify(cart));
  }, [cart, businessId]);

  useEffect(() => {
    if (!businessId) return;
    fetch(`${API_URL}/shopping/public-info/${businessId}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setShopInfo({
            name: data.name || '',
            email: data.email || '',
            phone: data.phone || '',
            logo: data.logo || '',
            bankDetails: data.bankDetails ? normalizeBankDetails(parseBankDetails(data.bankDetails), data.name) : null,
          });
        }
      })
      .catch(() => {});
  }, [businessId]);

  useEffect(() => {
    if (!businessId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const session = await syncShopSession(businessId);
        const token = session?.token ?? readShopToken(businessId);
        const customer = session?.customer ?? readShopCustomer(businessId);
        if (!cancelled && customer) {
          setShopCustomer(customer);
          setCustomerName(customer.name || '');
          setCustomerEmail(customer.email || '');
        }
        if (!cancelled) setShopToken(token);

        const headers: Record<string, string> = {};
        if (token) headers.Authorization = `Bearer ${token}`;
        const res = await fetch(`${API_URL}/products/public/${businessId}`, { headers });
        const data = await res.json();
        if (!cancelled) {
          setProducts(Array.isArray(data.products) ? data.products : []);
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setError('Could not load products. Please try again later.');
          setLoading(false);
        }
      }
    };

    load();
    return () => { cancelled = true; };
  }, [businessId]);

  const categories = useMemo(() => {
    const cats = Array.from(new Set(products.map(p => p.category).filter(Boolean)));
    return ['All', ...cats];
  }, [products]);

  const filtered = useMemo(() => {
    return products.filter(p => {
      const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase()) || (p.description || '').toLowerCase().includes(search.toLowerCase());
      const matchCat = selectedCategory === 'All' || p.category === selectedCategory;
      return matchSearch && matchCat;
    });
  }, [products, search, selectedCategory]);

  const addToCart = (product: Product, saleType: 'unit' | 'box' = 'unit') => {
    setCart(prev => {
      const existing = prev.find(c => c.product.id === product.id && c.saleType === saleType);
      if (existing) return prev.map(c => c.product.id === product.id && c.saleType === saleType ? { ...c, qty: c.qty + 1 } : c);
      return [...prev, { product, qty: 1, saleType }];
    });
  };

  const updateQty = (productId: string, saleType: 'unit' | 'box', qty: number) => {
    if (qty < 1) { removeFromCart(productId, saleType); return; }
    setCart(prev => prev.map(c => c.product.id === productId && c.saleType === saleType ? { ...c, qty } : c));
  };

  const removeFromCart = (productId: string, saleType: 'unit' | 'box') => {
    setCart(prev => prev.filter(c => !(c.product.id === productId && c.saleType === saleType)));
  };

  const getItemPrice = (c: CartItem): number => {
    if (c.saleType === 'box' && c.product.box_quantity && Number(c.product.box_quantity) > 0) {
      const bq = Number(c.product.box_quantity);
      // Wholesale customer or product has wholesale_price defined → use wholesale_price per unit × box_qty
      const wPrice = Number(c.product.wholesale_price);
      return (isWholesaleCustomer || wPrice > 0) && wPrice > 0
        ? wPrice * bq
        : Number(c.product.price) * bq;
    }
    // For wholesale customers buying units → show wholesale_price if available
    if (isWholesaleCustomer && c.product.wholesale_price && Number(c.product.wholesale_price) > 0) {
      return Number(c.product.wholesale_price);
    }
    return Number(c.product.price);
  };

  const cartSubtotal = cart.reduce((s, c) => s + getItemPrice(c) * c.qty, 0);
  const cartVat = cartSubtotal * VAT_RATE;
  const cartTotal = cartSubtotal + cartVat + shippingCost;
  const cartCount = cart.reduce((s, c) => s + c.qty, 0);

  const handleCheckout = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!customerName.trim()) { setCheckoutError('Please enter your name'); return; }
    if (!customerEmail.trim()) { setCheckoutError('Please enter your email'); return; }
    if (paymentMethod === 'Part Payment') {
      const dep = Number(depositAmount);
      if (!dep || dep <= 0 || dep >= cartTotal) { setCheckoutError('Enter a valid deposit amount (must be less than the total)'); return; }
    }
    setCheckoutError('');
    setSubmitting(true);
    try {
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (shopToken) headers.Authorization = `Bearer ${shopToken}`;
      const res = await fetch(`${API_URL}/shopping/public-order`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          businessId,
          customerId: shopCustomer?.id || null,
          customerName,
          customerEmail,
          customerPhone,
          notes: orderNotes || undefined,
          items: cart.map(c => ({
            productId: c.product.id,
            productName: c.product.name,
            quantity: c.qty,
            unitPrice: getItemPrice(c),
            saleType: c.saleType,
            box_quantity: c.saleType === 'box' ? c.product.box_quantity : null,
          })),
          subtotal: cartSubtotal,
          vat: cartVat,
          shippingCost,
          total: cartTotal,
          paymentMethod,
          depositAmount: paymentMethod === 'Part Payment' ? Number(depositAmount) : undefined,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        localStorage.removeItem(`cart_${businessId}`);
        setCart([]);
        setShowCart(false);
        navigate(`/shop/${businessId}/order-confirmed`, {
          state: {
            orderNumber: data.orderNumber,
            paymentMethod: data.paymentMethod,
            customerName,
            customerEmail,
            total: cartTotal,
            depositAmount: paymentMethod === 'Part Payment' ? Number(depositAmount) : undefined,
            bankDetails: data.bankDetails || null,
            businessEmail: data.businessEmail || null,
            businessPhone: data.businessPhone || null,
          },
        });
      } else {
        const err = await res.json();
        setCheckoutError(err.error || 'Failed to place order. Please try again.');
      }
    } catch {
      setCheckoutError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb' }}>
      <div style={{ textAlign: 'center', color: '#6b7280' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>🛍️</div>
        <p style={{ fontSize: 16, fontWeight: 600 }}>Loading shop…</p>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb' }}>
      <div style={{ textAlign: 'center', color: '#ef4444' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
        <p style={{ fontSize: 16, fontWeight: 600 }}>{error}</p>
      </div>
    </div>
  );

  if (orderPlaced) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0fdf4' }}>
      <div style={{ textAlign: 'center', background: '#fff', borderRadius: 16, padding: '48px 40px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)', maxWidth: 400 }}>
        <div style={{ fontSize: 56, marginBottom: 16 }}>🎉</div>
        <h2 style={{ margin: '0 0 12px', fontSize: 24, fontWeight: 800, color: '#166534' }}>Order Placed!</h2>
        <p style={{ margin: '0 0 24px', fontSize: 15, color: '#6b7280' }}>Thank you! Your order has been received. We'll contact you shortly at <strong>{customerEmail}</strong>.</p>
        <button onClick={() => { setOrderPlaced(false); setCustomerName(''); setCustomerEmail(''); setCustomerPhone(''); }} style={{ padding: '12px 28px', borderRadius: 8, border: 'none', background: '#3b82f6', color: '#fff', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>
          Continue Shopping
        </button>
      </div>
    </div>
  );

  return (
    <div className="shop-page">
      <header className="shop-banner">
        <div className="shop-banner-inner">
          <div className="shop-banner-top">
            <div className="shop-brand">
              {shopInfo?.logo
                ? <img src={shopInfo.logo} alt="" className="shop-logo" />
                : <div className="shop-logo-fallback">🛍️</div>
              }
              <div className="shop-brand-text">
                <h1>{businessName}</h1>
                <p className="shop-contact">
                  {shopInfo?.email && <a href={`mailto:${shopInfo.email}`}>{shopInfo.email}</a>}
                  {shopInfo?.phone && <a href={`tel:${shopInfo.phone.replace(/\s/g, '')}`}>{shopInfo.phone}</a>}
                </p>
              </div>
            </div>
            <div className="shop-actions">
              {shopCustomer ? (
                <>
                  <span style={{ fontSize: 12, opacity: 0.9 }}>👤 {shopCustomer.name}</span>
                  {isWholesaleCustomer && <span className="shop-chip shop-chip--trade">Trade</span>}
                  <button type="button" className="shop-btn" onClick={() => navigate(`/shop/${businessId}/dashboard`)}>My orders</button>
                  <button type="button" className="shop-btn" onClick={() => { clearShopSession(businessId!); window.location.reload(); }}>Logout</button>
                </>
              ) : (
                <>
                  <button type="button" className="shop-btn" onClick={() => navigate(`/shop/${businessId}/track`)}>Track order</button>
                  <button type="button" className="shop-btn" onClick={() => navigate(`/shop/${businessId}/login`)}>Login</button>
                  <button type="button" className="shop-btn shop-btn--primary" onClick={() => navigate(`/shop/${businessId}/register`)}>Register</button>
                </>
              )}
              <button type="button" className="shop-btn shop-btn--cart" onClick={() => setShowCart(true)}>
                🛒
                {cartCount > 0 && <span className="shop-cart-badge">{cartCount}</span>}
              </button>
            </div>
          </div>
          <div className="shop-banner-meta">
            <p className="shop-tagline">
              {isWholesaleCustomer
                ? `Welcome back — trade prices applied.`
                : `Browse & order online. Trade customer? Log in with your invite link for wholesale prices.`}
            </p>
            <div className="shop-chips">
              <span className="shop-chip">📦 {products.length} products</span>
              {categories.length > 1 && <span className="shop-chip">🗂️ {categories.length - 1} categories</span>}
              <span className="shop-chip">🚚 Fast dispatch</span>
            </div>
          </div>
        </div>
      </header>

      <main className="shop-main">
        <div className="shop-toolbar">
          <input
            type="search"
            className="shop-search"
            placeholder="Search products…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div className="shop-cats">
            {categories.map(cat => (
              <button
                key={cat}
                type="button"
                className={`shop-cat${selectedCategory === cat ? ' shop-cat--active' : ''}`}
                onClick={() => setSelectedCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 && (
          <div className="shop-empty">
            <div style={{ fontSize: 40, marginBottom: 8 }}>📦</div>
            <p>No products found.</p>
          </div>
        )}

        <div className="shop-grid">
          {filtered.map(p => {
            const images = getProductImages(p);
            const inCartUnit = cart.find(c => c.product.id === p.id && c.saleType === 'unit');
            const inCartBox = cart.find(c => c.product.id === p.id && c.saleType === 'box');
            const outOfStock = p.stock === 0;
            const hasBox = p.box_quantity != null && Number(p.box_quantity) > 0;
            const unitPrice = isWholesaleCustomer && p.wholesale_price && Number(p.wholesale_price) > 0
              ? Number(p.wholesale_price)
              : Number(p.price);
            const boxPrice = hasBox
              ? (isWholesaleCustomer && Number(p.wholesale_price) > 0
                ? Number(p.wholesale_price) * Number(p.box_quantity)
                : Number(p.price) * Number(p.box_quantity))
              : 0;
            return (
              <article
                key={p.id}
                className="shop-card"
                onMouseEnter={() => setHoveredProduct(p.id)}
                onMouseLeave={() => setHoveredProduct(null)}
                style={{ boxShadow: hoveredProduct === p.id ? '0 6px 20px rgba(0,0,0,0.1)' : undefined }}
              >
                <ProductImageGallery
                  images={images}
                  alt={p.name}
                  variant="card"
                  onImageClick={() => navigate(`/shop/${businessId}/product/${p.id}`)}
                />
                <div className="shop-card-body">
                  {p.category && <span style={{ fontSize: 10, fontWeight: 700, color: '#2563eb', textTransform: 'uppercase' }}>{p.category}</span>}
                  <h2 className="shop-card-name" onClick={() => navigate(`/shop/${businessId}/product/${p.id}`)}>{p.name}</h2>
                  {p.sku && <span className="shop-card-meta">SKU {p.sku}</span>}
                  <div>
                    <span className="shop-card-price">£{unitPrice.toFixed(2)}</span>
                    <span className="shop-card-meta"> / {p.unit || 'unit'}</span>
                    {hasBox && <div className="shop-card-meta" style={{ color: '#7c3aed', fontWeight: 600 }}>Box ({p.box_quantity}): £{boxPrice.toFixed(2)}</div>}
                  </div>
                  {outOfStock
                    ? <span style={{ fontSize: 11, color: '#ef4444', fontWeight: 600 }}>Out of stock</span>
                    : p.stock > 0 && p.stock < 10
                      ? <span style={{ fontSize: 11, color: '#f97316' }}>Only {p.stock} left</span>
                      : null
                  }
                </div>
                <div className="shop-card-actions">
                  {outOfStock ? (
                    <button type="button" className="shop-add" disabled>Out of stock</button>
                  ) : inCartUnit ? (
                    <div className="shop-qty-row">
                      <span style={{ fontSize: 11, color: '#6b7280', minWidth: 28 }}>Unit</span>
                      <button type="button" className="shop-qty-btn" onClick={() => updateQty(p.id, 'unit', inCartUnit.qty - 1)}>−</button>
                      <span style={{ flex: 1, textAlign: 'center', fontWeight: 700, fontSize: 13 }}>{inCartUnit.qty}</span>
                      <button type="button" className="shop-qty-btn" onClick={() => updateQty(p.id, 'unit', inCartUnit.qty + 1)}>+</button>
                    </div>
                  ) : (
                    <button type="button" className="shop-add" onClick={() => addToCart(p, 'unit')}>+ Add — £{unitPrice.toFixed(2)}</button>
                  )}
                  {hasBox && !outOfStock && (
                    inCartBox ? (
                      <div className="shop-qty-row">
                        <span style={{ fontSize: 11, color: '#7c3aed', minWidth: 28 }}>Box</span>
                        <button type="button" className="shop-qty-btn" onClick={() => updateQty(p.id, 'box', inCartBox.qty - 1)}>−</button>
                        <span style={{ flex: 1, textAlign: 'center', fontWeight: 700, fontSize: 13 }}>{inCartBox.qty}</span>
                        <button type="button" className="shop-qty-btn" onClick={() => updateQty(p.id, 'box', inCartBox.qty + 1)}>+</button>
                      </div>
                    ) : (
                      <button type="button" className="shop-add shop-add--box" onClick={() => addToCart(p, 'box')}>📦 Box — £{boxPrice.toFixed(2)}</button>
                    )
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </main>

      <footer className="shop-footer">
        {businessName} · Secure checkout · Powered by ShahoSuite
      </footer>

      {/* Cart Drawer */}
      {showCart && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 9999 }}>
          {/* Overlay */}
          <div onClick={() => setShowCart(false)} style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} />
          {/* Drawer */}
          <div style={{ position: 'absolute', top: 0, right: 0, bottom: 0, width: Math.min(480, window.innerWidth), background: '#fff', display: 'flex', flexDirection: 'column', boxShadow: '-4px 0 24px rgba(0,0,0,0.15)' }}>
            {/* Drawer header */}
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#111827' }}>🛒 Your Cart {cartCount > 0 && `(${cartCount})`}</h2>
              <button onClick={() => setShowCart(false)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#6b7280' }}>✕</button>
            </div>

            {/* Cart items */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
              {cart.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '60px 0', color: '#9ca3af' }}>
                  <div style={{ fontSize: 48, marginBottom: 12 }}>🛒</div>
                  <p style={{ fontSize: 15 }}>Your cart is empty</p>
                </div>
              ) : (
                <>
                  {cart.map(c => {
                    const itemPrice = getItemPrice(c);
                    const cartKey = `${c.product.id}-${c.saleType}`;
                    return (
                    <div key={cartKey} style={{ display: 'flex', gap: 12, marginBottom: 16, paddingBottom: 16, borderBottom: '1px solid #f3f4f6' }}>
                      <div style={{ width: 56, height: 56, borderRadius: 8, background: '#f3f4f6', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                        {c.product.image1 || c.product.image
                          ? <img src={c.product.image1 || c.product.image} alt={c.product.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          : <span style={{ fontSize: 24 }}>📦</span>
                        }
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: '#111827', marginBottom: 2 }}>{c.product.name}</div>
                        {c.saleType === 'box' && <div style={{ fontSize: 11, color: '#8b5cf6', fontWeight: 600, marginBottom: 2 }}>📦 Box of {c.product.box_quantity}</div>}
                        <div style={{ fontWeight: 700, color: '#1d4ed8' }}>£{(itemPrice * c.qty).toFixed(2)}</div>
                        <div style={{ fontSize: 11, color: '#9ca3af' }}>£{itemPrice.toFixed(2)} × {c.qty}</div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <button onClick={() => updateQty(c.product.id, c.saleType, c.qty - 1)} style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 16 }}>−</button>
                          <span style={{ fontWeight: 700, minWidth: 20, textAlign: 'center' }}>{c.qty}</span>
                          <button onClick={() => updateQty(c.product.id, c.saleType, c.qty + 1)} style={{ width: 28, height: 28, borderRadius: 6, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 16 }}>+</button>
                        </div>
                        <button onClick={() => removeFromCart(c.product.id, c.saleType)} style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>Remove</button>
                      </div>
                    </div>
                    );
                  })}

                  {/* Checkout form — UK wholesale order */}
                  <div style={{ background: '#f9fafb', borderRadius: 12, padding: 20, marginTop: 8 }}>
                    <div style={{ marginBottom: 14, fontSize: 13 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', color: '#6b7280' }}><span>Subtotal (ex VAT)</span><span>£{cartSubtotal.toFixed(2)}</span></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', color: '#6b7280' }}><span>VAT (20%)</span><span>£{cartVat.toFixed(2)}</span></div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0 4px', borderTop: '1px solid #e5e7eb', marginTop: 4, fontWeight: 800, fontSize: 16, color: '#111827' }}><span>Total</span><span>£{cartTotal.toFixed(2)}</span></div>
                    </div>

                    <h3 style={{ margin: '0 0 12px', fontSize: 14, fontWeight: 700, color: '#374151' }}>Your details</h3>
                    <form onSubmit={handleCheckout} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {!shopCustomer && <>
                        <input value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Full name *" style={inputStyle} required />
                        <input type="email" value={customerEmail} onChange={e => setCustomerEmail(e.target.value)} placeholder="Email address *" style={inputStyle} required />
                      </>}
                      {shopCustomer && (
                        <div style={{ padding: '8px 12px', background: '#eff6ff', borderRadius: 8, fontSize: 13, color: '#1d4ed8', fontWeight: 600 }}>
                          Ordering as {shopCustomer.name} ({shopCustomer.email})
                        </div>
                      )}
                      <input value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} placeholder="Phone (optional)" style={inputStyle} />
                      <textarea
                        value={orderNotes}
                        onChange={e => setOrderNotes(e.target.value)}
                        placeholder="Order notes (optional)"
                        rows={2}
                        style={{ ...inputStyle, resize: 'vertical', minHeight: 52, fontFamily: 'inherit', fontSize: 13 }}
                      />

                      <div>
                        <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 8 }}>Payment method</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {([
                            { value: 'Bank Transfer' as const, label: 'Bank transfer', desc: 'Pay by bank transfer — use reference on your payment' },
                            { value: 'Cash on Collection' as const, label: 'Cash on collection', desc: 'Pay when you collect from us' },
                            { value: 'Part Payment' as const, label: 'Deposit / part payment', desc: 'Pay a deposit now — we agree the balance with you' },
                          ]).map(opt => (
                            <label key={opt.value} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', border: `2px solid ${paymentMethod === opt.value ? '#1e3a8a' : '#e5e7eb'}`, borderRadius: 8, cursor: 'pointer', background: paymentMethod === opt.value ? '#eff6ff' : '#fff' }}>
                              <input type="radio" name="paymentMethod" value={opt.value} checked={paymentMethod === opt.value} onChange={() => setPaymentMethod(opt.value)} style={{ marginTop: 2, flexShrink: 0 }} />
                              <div>
                                <div style={{ fontWeight: 700, fontSize: 13, color: '#111827' }}>{opt.label}</div>
                                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 1 }}>{opt.desc}</div>
                              </div>
                            </label>
                          ))}
                        </div>

                        {paymentMethod === 'Bank Transfer' && (
                          <div style={{ marginTop: 12, padding: '12px 14px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, fontSize: 13, color: '#1e3a8a' }}>
                            {hasCompleteBankDetails(shopInfo?.bankDetails) ? (
                              <>
                                <div style={{ fontWeight: 700, marginBottom: 8 }}>Bank details (reference = your order number)</div>
                                <div style={{ lineHeight: 1.7 }}>
                                  <div><span style={{ color: '#6b7280' }}>Account name:</span> <strong>{shopInfo!.bankDetails!.bank_account_name || shopInfo!.bankDetails!.bank_name}</strong></div>
                                  <div><span style={{ color: '#6b7280' }}>Sort code:</span> <strong>{shopInfo!.bankDetails!.bank_sort_code}</strong></div>
                                  <div><span style={{ color: '#6b7280' }}>Account number:</span> <strong>{shopInfo!.bankDetails!.bank_account_number}</strong></div>
                                </div>
                                <p style={{ margin: '8px 0 0', fontSize: 12, color: '#3b82f6' }}>Full details will also be sent by email after you place the order.</p>
                              </>
                            ) : (
                              <p style={{ margin: 0, color: '#92400e' }}>
                                Bank details will be sent by email after ordering
                                {shopInfo?.email ? `, or contact ${shopInfo.email}` : ''}.
                              </p>
                            )}
                          </div>
                        )}

                        {paymentMethod === 'Cash on Collection' && (
                          <div style={{ marginTop: 12, padding: '12px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, fontSize: 13, color: '#166534' }}>
                            Pay <strong>£{cartTotal.toFixed(2)}</strong> in cash when you collect. We will contact you to arrange collection.
                          </div>
                        )}

                        {paymentMethod === 'Part Payment' && (
                          <div style={{ marginTop: 12 }}>
                            <div style={{ padding: '12px 14px', background: '#fefce8', border: '1px solid #fde68a', borderRadius: 8, fontSize: 13, color: '#92400e', marginBottom: 10 }}>
                              Enter the deposit you will pay now. We will contact you about the remaining balance.
                            </div>
                            <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>Deposit amount (£) *</label>
                            <input
                              type="number"
                              min={1}
                              max={cartTotal - 0.01}
                              step={0.01}
                              value={depositAmount}
                              onChange={e => setDepositAmount(e.target.value)}
                              placeholder={`e.g. ${(cartTotal * 0.5).toFixed(2)}`}
                              style={{ ...inputStyle, borderColor: '#f59e0b' }}
                              required
                            />
                            {depositAmount && Number(depositAmount) > 0 && (
                              <p style={{ fontSize: 12, color: '#6b7280', margin: '4px 0 0' }}>
                                Remaining: £{(cartTotal - Number(depositAmount)).toFixed(2)}
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      {checkoutError && <p style={{ color: '#ef4444', fontSize: 13, margin: 0 }}>⚠️ {checkoutError}</p>}
                      <button type="submit" disabled={submitting} style={{ padding: '13px 0', borderRadius: 8, border: 'none', background: '#16a34a', color: '#fff', fontWeight: 800, fontSize: 15, cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1, marginTop: 4 }}>
                        {submitting ? 'Placing order…' : paymentMethod === 'Bank Transfer' ? `Place order — pay £${cartTotal.toFixed(2)} by transfer` : `Place order — £${cartTotal.toFixed(2)}`}
                      </button>
                      {!shopCustomer && (
                        <p style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', margin: 0 }}>
                          <button type="button" onClick={() => { setShowCart(false); navigate(`/shop/${businessId}/login`); }} style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>Login</button> or <button type="button" onClick={() => { setShowCart(false); navigate(`/shop/${businessId}/register`); }} style={{ background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>Register</button> to track your orders
                        </p>
                      )}
                    </form>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const inputStyle: React.CSSProperties = {
  padding: '9px 12px', borderRadius: 8, border: '1.5px solid #e5e7eb',
  fontSize: 14, outline: 'none', background: '#fff', width: '100%', boxSizing: 'border-box',
};

export default PublicShop;
